/**
 * Segmentation meshes and line assets (SurfaceObject.cs): glTF geometries in file mm are moved
 * to the scene frame, drawn transparent premultiplied with ZWrite off, coloured by name through
 * DEFAULT_COLORS, metallic/glossiness per kind (cusp 0.3/0.5, aorta 0/0.8). Every segmentation
 * row starts hidden (inventory default-off).
 */
import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import type { AssetEntry } from '@/api/client';
import { loadPolyline, loadSurfaceGeometries } from '@/assets/loader';
import { DEFAULT_COLORS, type Rgba } from '@/inventory/colors';
import { LAYER_MAIN, LAYER_OVERLAY } from '@/theater/Viewer';

/** File (x, y, z) -> scene (x, z, y): a permutation with negative determinant. */
export const FILE_TO_SCENE = new THREE.Matrix4().set(1, 0, 0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 0, 0, 0, 1);

export interface SurfaceStyle {
  colorKey: string;
  rowKey: string; // inventory segmentation row
  metallic: number;
  glossiness: number;
  isCusp: boolean;
}

/** SurfaceObject name pass (first match wins, lowercase contains unless noted). */
export function styleForName(name: string): SurfaceStyle | null {
  const n = name.toLowerCase();
  const aorta = (colorKey: string, rowKey = colorKey): SurfaceStyle => ({ colorKey, rowKey, metallic: 0, glossiness: 0.8, isCusp: false });
  const cusp = (colorKey: string, rowKey = colorKey): SurfaceStyle => ({ colorKey, rowKey, metallic: 0.3, glossiness: 0.5, isCusp: true });
  if (n.includes('aorta/lv')) return aorta('AortaLvSegmentation');
  if (n.includes('mid ascending aorta')) return aorta('MidAscendingAortaSegmentation');
  if (n.includes('distal ascending aorta')) return aorta('DistalAscendingAortaSegmentation');
  if (n.includes('mid transverse arch')) return aorta('MidTransverseArchSegmentation');
  if (n.includes('mid descending aorta')) return aorta('MidDescendingAortaSegmentation');
  if (n.includes('aorta')) return aorta('AorticRoot');
  if (n.includes('left atrium')) return aorta('LeftAtriumSegmentation');
  if (n.includes('right atrium')) return aorta('RightAtriumSegmentation');
  if (n.includes('pulmonary artery')) return aorta('PulmonaryArterySegmentation');
  if (n.includes('model leaflet 1')) return cusp('LeafletNModelSegmentation');
  if (n.includes('model leaflet 2')) return cusp('LeafletRModelSegmentation');
  if (n.includes('model leaflet 3')) return cusp('LeafletLModelSegmentation');
  if (n.includes('leaflet 1')) return cusp('LeafletNSegmentation');
  if (n.includes('leaflet 2')) return cusp('LeafletRSegmentation');
  if (n.includes('leaflet 3')) return cusp('LeafletLSegmentation');
  if (n.includes('calcium')) return aorta('CalciumSegmentation');
  if (n.includes('cusp 1')) return cusp('NSinus');
  if (n.includes('cusp 2')) return cusp('RSinus');
  if (n.includes('cusp 3')) return cusp('LSinus');
  if (n.includes('lvot')) return aorta('LVOT');
  if (n.includes('coronaries')) return aorta('CoronariesSegmentation');
  if (n === 'vbr' || n.includes('vbr segmentation')) return aorta('VbrSegmentation');
  if (n === 'stj' || n.includes('stj segmentation')) return aorta('StjSegmentation');
  if (n.includes('sov')) return aorta('SovSegmentation');
  if (n.includes('commissure')) return aorta('CommissuresSegmentation');
  if (n.includes('coaptation deficiency')) return aorta('CoaptationDeficiencySegmentation');
  if (n.includes('coaptation') || n.includes('coapt')) return aorta('CoaptationSegmentation');
  if (n.includes('ostia')) return aorta('OstiaSegmentation');
  return null;
}

export interface SurfaceItem {
  asset: AssetEntry;
  name: string;
  style: SurfaceStyle | null;
  object: THREE.Object3D;
  materials: THREE.Material[];
  isLine: boolean;
  color: Rgba;
  boundsMm: THREE.Box3;
  vertices?: THREE.Vector3[]; // polylines, scene mm
}

export class SurfaceSet {
  readonly group = new THREE.Group();
  readonly items: SurfaceItem[] = [];
  /** Assets that failed to load (fetch, decode), for the UI: a missing aorta mesh silently
   * disables contour re-slicing and surface snapping otherwise. */
  readonly failed: { name: string; reason: string }[] = [];
  private lineMaterials: LineMaterial[] = [];

  constructor() {
    this.group.name = 'surfaces';
  }

  async load(assets: AssetEntry[], onItem?: (item: SurfaceItem) => void): Promise<void> {
    await Promise.all(
      assets.map(async (asset) => {
        try {
          const item = asset.flags.includes('line') ? await this.loadLine(asset) : await this.loadMesh(asset);
          if (item) {
            this.items.push(item);
            this.group.add(item.object);
            onItem?.(item);
          }
        } catch (e) {
          console.warn(`surface ${asset.name} failed to load`, e);
          this.failed.push({ name: asset.name, reason: (e as Error)?.message ?? String(e) });
        }
      }),
    );
    this.items.sort((a, b) => a.name.localeCompare(b.name));
  }

  private async loadMesh(asset: AssetEntry): Promise<SurfaceItem | null> {
    const geoms = await loadSurfaceGeometries(asset);
    if (!geoms.length) return null;
    const style = styleForName(asset.name);
    const rgba: Rgba = style ? DEFAULT_COLORS[style.colorKey] : asset.color ? (asset.color as Rgba) : [0.7, 0.7, 0.7, 190 / 255];
    const container = new THREE.Group();
    container.name = asset.name;
    const materials: THREE.Material[] = [];
    const bounds = new THREE.Box3();
    const doubleSided = asset.flags.includes('double_sided');
    for (const g of geoms) {
      g.applyMatrix4(FILE_TO_SCENE);
      flipWinding(g); // the permutation mirrors; keep front faces CCW
      g.computeBoundingBox();
      bounds.union(g.boundingBox!);
      const mat = new THREE.MeshStandardMaterial({
        color: new THREE.Color(rgba[0], rgba[1], rgba[2]),
        opacity: rgba[3],
        transparent: true,
        premultipliedAlpha: true,
        depthWrite: false,
        metalness: style?.metallic ?? 0,
        roughness: 1 - (style?.glossiness ?? 0),
        side: doubleSided ? THREE.DoubleSide : THREE.FrontSide,
      });
      const mesh = new THREE.Mesh(g, mat);
      mesh.renderOrder = style?.isCusp ? 2 : 1; // queue 3999 for all; cusps on top (desktop ZTest Always)
      mesh.layers.set(LAYER_MAIN);
      container.add(mesh);
      materials.push(mat);
    }
    container.visible = false; // segmentation rows start hidden
    return { asset, name: asset.name, style, object: container, materials, isLine: false, color: rgba, boundsMm: bounds };
  }

  private async loadLine(asset: AssetEntry): Promise<SurfaceItem | null> {
    const data = await loadPolyline(asset);
    const vertices = data.vertices.map(([x, y, z]) => new THREE.Vector3(x, z, y));
    const isCenterline = asset.name.toLowerCase().startsWith('centerline');
    const rgba: Rgba = isCenterline ? DEFAULT_COLORS.Centerline : DEFAULT_COLORS.AortaCurve;
    const container = new THREE.Group();
    container.name = asset.name;
    const materials: THREE.Material[] = [];
    const bounds = new THREE.Box3().setFromPoints(vertices);
    for (const seg of data.lines) {
      const positions: number[] = [];
      for (const i of seg) positions.push(vertices[i].x, vertices[i].y, vertices[i].z);
      // join consecutive segments into one polyline when they chain
      const geometry = new LineGeometry();
      geometry.setPositions(positions);
      const material = new LineMaterial({
        color: new THREE.Color(rgba[0], rgba[1], rgba[2]).getHex(),
        linewidth: 0.003, // 0.3 mm in world units (1 = 100 mm)
        worldUnits: true,
        transparent: true,
        opacity: rgba[3],
        depthTest: false,
        depthWrite: false,
        side: THREE.DoubleSide, // view-space quads under the flipped root: see PredictionObject
      });
      const line = new Line2(geometry, material);
      line.computeLineDistances();
      line.renderOrder = 20;
      line.layers.set(LAYER_OVERLAY);
      container.add(line);
      materials.push(material);
      this.lineMaterials.push(material);
    }
    container.visible = false; // Root Centroid Line is default-off
    return { asset, name: asset.name, style: null, object: container, materials, isLine: true, color: rgba, boundsMm: bounds, vertices };
  }

  /** Replace a polyline item's drawn vertices (the rebuilt Root Centroid), or restore the
   * pipeline's with null. `item.vertices` always keeps the pipeline polyline. */
  setPolyline(item: SurfaceItem, points: THREE.Vector3[] | null): void {
    if (!item.isLine) return;
    const pts = points && points.length >= 2 ? points : (item.vertices ?? []);
    const lines = item.object.children.filter((c) => (c as Line2).isLine2) as Line2[];
    if (!lines.length) return;
    const first = lines[0];
    for (const extra of lines.slice(1)) {
      item.object.remove(extra);
      extra.geometry.dispose();
    }
    const positions: number[] = [];
    for (const p of pts) positions.push(p.x, p.y, p.z);
    const geometry = new LineGeometry();
    geometry.setPositions(positions);
    first.geometry.dispose();
    first.geometry = geometry;
    first.computeLineDistances();
  }

  /** Line2 materials need the viewport resolution for correct widths. */
  setResolution(width: number, height: number): void {
    for (const m of this.lineMaterials) m.resolution.set(width, height);
  }

  setColor(item: SurfaceItem, rgba: Rgba): void {
    item.color = rgba;
    for (const m of item.materials) {
      if ((m as LineMaterial).isLineMaterial) {
        (m as LineMaterial).color.setRGB(rgba[0], rgba[1], rgba[2]);
        m.opacity = rgba[3];
      } else {
        (m as THREE.MeshStandardMaterial).color.setRGB(rgba[0], rgba[1], rgba[2]);
        m.opacity = rgba[3];
      }
    }
  }

  find(predicate: (item: SurfaceItem) => boolean): SurfaceItem | undefined {
    return this.items.find(predicate);
  }

  byRole(role: string): SurfaceItem | undefined {
    return this.items.find((i) => i.asset.meta?.role === role);
  }

  dispose(): void {
    for (const item of this.items) {
      item.object.traverse((o) => {
        const m = o as THREE.Mesh;
        if (m.geometry) m.geometry.dispose();
      });
      for (const m of item.materials) m.dispose();
    }
  }
}

function flipWinding(g: THREE.BufferGeometry): void {
  const index = g.getIndex();
  if (index) {
    const a = index.array;
    for (let i = 0; i + 2 < a.length; i += 3) {
      const t = a[i + 1];
      a[i + 1] = a[i + 2];
      a[i + 2] = t;
    }
    index.needsUpdate = true;
  } else {
    const pos = g.getAttribute('position');
    const nrm = g.getAttribute('normal');
    const swap = (attr: THREE.BufferAttribute | THREE.InterleavedBufferAttribute, i: number, j: number) => {
      const x = attr.getX(i);
      const y = attr.getY(i);
      const z = attr.getZ(i);
      attr.setXYZ(i, attr.getX(j), attr.getY(j), attr.getZ(j));
      attr.setXYZ(j, x, y, z);
    };
    for (let i = 0; i + 2 < pos.count; i += 3) {
      swap(pos, i + 1, i + 2);
      if (nrm) swap(nrm, i + 1, i + 2);
    }
    pos.needsUpdate = true;
    if (nrm) nrm.needsUpdate = true;
  }
}
